import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';
import ToggleSwitch from '../components/ToggleSwitch';

const PAGES = [
  { label: 'Home', path: '/' },
  { label: 'Adopt a Cow', path: '/adopt-a-cow' },
  { label: 'About Us', path: '/about-us' },
  { label: 'Management', path: '/management' },
  { label: 'Our Activities', path: '/activities' },
  { label: 'Donations', path: '/donations' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Videos', path: '/videos' },
  { label: 'Slokas', path: '/slokas' },
  { label: 'FAQs', path: '/faqs' },
];

const EMPTY_QR = { label: '', type: 'route', value: '/', order: 0 };
const EMPTY_FAQ = { question: '', keywords: '', answer: '', order: 0 };

export default function ManageChatbot() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Inline edit state
  const [qrForm, setQrForm] = useState(null);   // null | { index: number|null, data: {...} }
  const [faqForm, setFaqForm] = useState(null); // null | { index: number|null, data: {...} }

  useEffect(() => {
    API.get('/chatbot')
      .then(r => { setConfig(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const saveAll = async (patch = {}) => {
    setSaving(true);
    try {
      const payload = { ...config, ...patch };
      const r = await API.put('/chatbot', payload);
      setConfig(r.data);
      toast.success('Chatbot settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  // ── Quick Replies ──────────────────────────────────────────
  const openAddQr = () => setQrForm({ index: null, data: { ...EMPTY_QR } });
  const openEditQr = (i) => {
    const qr = config.quickReplies[i];
    setQrForm({ index: i, data: { label: qr.label, type: qr.type, value: qr.value, order: qr.order } });
  };
  const cancelQr = () => setQrForm(null);

  const saveQr = () => {
    if (!qrForm.data.label.trim()) return toast.error('Label is required');
    if (!qrForm.data.value.trim()) return toast.error('Action/answer is required');
    const updated = [...(config.quickReplies || [])];
    if (qrForm.index === null) {
      updated.push({ ...qrForm.data, order: updated.length });
    } else {
      updated[qrForm.index] = { ...updated[qrForm.index], ...qrForm.data };
    }
    const next = { ...config, quickReplies: updated };
    setConfig(next);
    setQrForm(null);
    saveAll({ quickReplies: updated });
  };

  const deleteQr = (i) => {
    if (!window.confirm('Remove this quick reply?')) return;
    const updated = config.quickReplies.filter((_, idx) => idx !== i);
    const next = { ...config, quickReplies: updated };
    setConfig(next);
    saveAll({ quickReplies: updated });
  };

  // ── FAQs ──────────────────────────────────────────────────
  const openAddFaq = () => setFaqForm({ index: null, data: { ...EMPTY_FAQ } });
  const openEditFaq = (i) => {
    const faq = config.faqs[i];
    setFaqForm({ index: i, data: { question: faq.question, keywords: (faq.keywords || []).join(', '), answer: faq.answer, order: faq.order } });
  };
  const cancelFaq = () => setFaqForm(null);

  const saveFaq = () => {
    if (!faqForm.data.question.trim()) return toast.error('Question is required');
    if (!faqForm.data.answer.trim()) return toast.error('Answer is required');
    const keywords = faqForm.data.keywords.split(',').map(k => k.trim()).filter(Boolean);
    const updated = [...(config.faqs || [])];
    if (faqForm.index === null) {
      updated.push({ ...faqForm.data, keywords, order: updated.length });
    } else {
      updated[faqForm.index] = { ...updated[faqForm.index], ...faqForm.data, keywords };
    }
    const next = { ...config, faqs: updated };
    setConfig(next);
    setFaqForm(null);
    saveAll({ faqs: updated });
  };

  const deleteFaq = (i) => {
    if (!window.confirm('Remove this FAQ?')) return;
    const updated = config.faqs.filter((_, idx) => idx !== i);
    const next = { ...config, faqs: updated };
    setConfig(next);
    saveAll({ faqs: updated });
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;
  if (!config) return <div style={{ padding: 32, color: '#888' }}>Could not load chatbot config.</div>;

  return (
    <div>
      <h2 className="admin-page-title">💬 Manage Chatbot</h2>

      {/* ── Basic Settings ─────────────────────────────────── */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>Basic Settings</h3>

        <div style={{ marginBottom: 16 }}>
          <ToggleSwitch
            checked={config.enabled}
            onChange={v => setConfig(c => ({ ...c, enabled: v }))}
            onLabel="Chatbot Enabled"
            offLabel="Chatbot Disabled"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Bot Name</label>
            <input className="form-input" value={config.botName} onChange={e => setConfig(c => ({ ...c, botName: e.target.value }))} placeholder="e.g. Nandi" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Greeting Message</label>
          <textarea className="form-input" rows={2} value={config.greeting} onChange={e => setConfig(c => ({ ...c, greeting: e.target.value }))} />
        </div>

        <div className="form-group">
          <label className="form-label">Fallback Message <span style={{ color: '#999', fontSize: '0.8rem' }}>(shown when no FAQ matches)</span></label>
          <textarea className="form-input" rows={2} value={config.fallbackMessage} onChange={e => setConfig(c => ({ ...c, fallbackMessage: e.target.value }))} />
        </div>

        <button className="btn btn-primary" onClick={() => saveAll()} disabled={saving}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>

      {/* ── Quick Replies ──────────────────────────────────── */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontWeight: 700, margin: 0 }}>Quick Reply Buttons</h3>
          <button className="btn btn-primary btn-sm" onClick={openAddQr}>+ Add</button>
        </div>
        <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 14 }}>
          These appear as clickable chips after the bot's greeting. Each one can route the user to a page or show a custom answer.
        </p>

        {qrForm && qrForm.index === null && (
          <QrFormInline form={qrForm.data} onChange={d => setQrForm(f => ({ ...f, data: d }))} onSave={saveQr} onCancel={cancelQr} isNew />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(config.quickReplies || []).map((qr, i) => (
            qrForm?.index === i ? (
              <QrFormInline key={i} form={qrForm.data} onChange={d => setQrForm(f => ({ ...f, data: d }))} onSave={saveQr} onCancel={cancelQr} />
            ) : (
              <div key={i} className="chatbot-list-row">
                <div style={{ flex: 1 }}>
                  <strong>{qr.label}</strong>
                  <span style={{ marginLeft: 10, fontSize: '0.78rem', background: qr.type === 'route' ? '#e3f2fd' : '#f3e5f5', color: qr.type === 'route' ? '#1565c0' : '#6a1b9a', borderRadius: 4, padding: '2px 8px' }}>
                    {qr.type === 'route' ? `→ ${qr.value}` : 'Custom answer'}
                  </span>
                </div>
                <button className="btn btn-sm btn-outline" onClick={() => openEditQr(i)}>✏️ Edit</button>
                <button className="btn btn-sm" style={{ background: '#ffeaea', color: '#c62828', border: '1px solid #ffcdd2' }} onClick={() => deleteQr(i)}>✕</button>
              </div>
            )
          ))}
          {config.quickReplies?.length === 0 && <p style={{ color: '#aaa', fontSize: '0.85rem' }}>No quick replies yet. Add one above.</p>}
        </div>
      </div>

      {/* ── FAQ Responses ─────────────────────────────────── */}
      <div className="admin-form-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontWeight: 700, margin: 0 }}>FAQ Responses</h3>
          <button className="btn btn-primary btn-sm" onClick={openAddFaq}>+ Add</button>
        </div>
        <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 14 }}>
          When a user types a message, the bot scans these keywords to find the best answer. Add comma-separated keywords for each topic.
        </p>

        {faqForm && faqForm.index === null && (
          <FaqFormInline form={faqForm.data} onChange={d => setFaqForm(f => ({ ...f, data: d }))} onSave={saveFaq} onCancel={cancelFaq} isNew />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(config.faqs || []).map((faq, i) => (
            faqForm?.index === i ? (
              <FaqFormInline key={i} form={faqForm.data} onChange={d => setFaqForm(f => ({ ...f, data: d }))} onSave={saveFaq} onCancel={cancelFaq} />
            ) : (
              <div key={i} className="chatbot-list-row" style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <strong style={{ display: 'block', marginBottom: 3 }}>{faq.question}</strong>
                  <span style={{ fontSize: '0.76rem', color: '#888' }}>Keywords: {(faq.keywords || []).join(', ') || '—'}</span>
                  <p style={{ margin: '4px 0 0', fontSize: '0.83rem', color: '#555' }}>{faq.answer}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => openEditFaq(i)}>✏️ Edit</button>
                  <button className="btn btn-sm" style={{ background: '#ffeaea', color: '#c62828', border: '1px solid #ffcdd2' }} onClick={() => deleteFaq(i)}>✕</button>
                </div>
              </div>
            )
          ))}
          {config.faqs?.length === 0 && <p style={{ color: '#aaa', fontSize: '0.85rem' }}>No FAQ responses yet. Add one above.</p>}
        </div>
      </div>
    </div>
  );
}

function QrFormInline({ form, onChange, onSave, onCancel, isNew }) {
  return (
    <div style={{ background: '#f9f9f9', border: '1.5px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 10 }}>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Button Label *</label>
          <input className="form-input" value={form.label} onChange={e => onChange({ ...form, label: e.target.value })} placeholder="e.g. 🙏 How to Donate?" />
        </div>
        <div className="form-group">
          <label className="form-label">Action Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className={`btn btn-sm ${form.type === 'route' ? 'btn-primary' : 'btn-outline'}`} onClick={() => onChange({ ...form, type: 'route', value: '/' })}>Navigate to Page</button>
            <button type="button" className={`btn btn-sm ${form.type === 'faq' ? 'btn-primary' : 'btn-outline'}`} onClick={() => onChange({ ...form, type: 'faq', value: '' })}>Custom Answer</button>
          </div>
        </div>
      </div>
      {form.type === 'route' ? (
        <div className="form-group">
          <label className="form-label">Page</label>
          <select className="form-select" value={form.value} onChange={e => onChange({ ...form, value: e.target.value })}>
            {PAGES.map(p => <option key={p.path} value={p.path}>{p.label} ({p.path})</option>)}
          </select>
        </div>
      ) : (
        <div className="form-group">
          <label className="form-label">Answer Text *</label>
          <textarea className="form-input" rows={2} value={form.value} onChange={e => onChange({ ...form, value: e.target.value })} placeholder="The response to show the user" />
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={onSave}>{isNew ? 'Add' : 'Save'}</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function FaqFormInline({ form, onChange, onSave, onCancel, isNew }) {
  return (
    <div style={{ background: '#f9f9f9', border: '1.5px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 10 }}>
      <div className="form-group">
        <label className="form-label">Topic / Question *</label>
        <input className="form-input" value={form.question} onChange={e => onChange({ ...form, question: e.target.value })} placeholder="e.g. How to donate?" />
      </div>
      <div className="form-group">
        <label className="form-label">Keywords <span style={{ color: '#999', fontSize: '0.8rem' }}>(comma-separated — these trigger this FAQ)</span></label>
        <input className="form-input" value={form.keywords} onChange={e => onChange({ ...form, keywords: e.target.value })} placeholder="donate, donation, pay, upi" />
      </div>
      <div className="form-group">
        <label className="form-label">Answer *</label>
        <textarea className="form-input" rows={3} value={form.answer} onChange={e => onChange({ ...form, answer: e.target.value })} placeholder="The response the bot will show when keywords are matched" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={onSave}>{isNew ? 'Add' : 'Save'}</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
